import { useCallback, useEffect, useState } from 'react';
import { IconTick } from '../components/Icons';
import { Alert, Loading, PageHeader, Pager } from '../App';
import {
  adminApi,
  dateTime,
  errorMessage,
  humanise,
  type AdminUser,
  type PagedResult,
} from '../api/admin';

export function UsersPage() {
  const [data, setData] = useState<PagedResult<AdminUser> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [suspending, setSuspending] = useState<AdminUser | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setData(
        await adminApi.users({
          keyword: keyword || undefined,
          role: role || undefined,
          status: status || undefined,
          page,
          pageSize: 20,
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [keyword, role, status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const reactivate = async (user: AdminUser) => {
    try {
      await adminApi.reactivateUser(user.id);
      setSuccess(`${user.email} has been reactivated.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const verify = async (user: AdminUser) => {
    try {
      await adminApi.verifyContact(user.id);
      setSuccess(`${user.email} marked as contact-verified.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Users & owners"
        description="Accounts on the platform. Credentials and tokens are never exposed here."
      />

      <Alert message={error} />
      <Alert message={success} variant="success" />

      <div className="admin-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-5">
            <label className="form-label small mb-1" htmlFor="user-search">
              Search
            </label>
            <input
              id="user-search"
              className="form-control form-control-sm"
              placeholder="Name, email or phone"
              defaultValue={keyword}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  setKeyword((e.target as HTMLInputElement).value);
                }
              }}
              onBlur={(e) => {
                setPage(1);
                setKeyword(e.target.value);
              }}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="user-role">
              Role
            </label>
            <select
              id="user-role"
              className="form-select form-select-sm"
              value={role}
              onChange={(e) => {
                setPage(1);
                setRole(e.target.value);
              }}
            >
              <option value="">All roles</option>
              <option value="Customer">Customer</option>
              <option value="Owner">Owner</option>
              <option value="SuperAdmin">Super admin</option>
            </select>
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="user-status">
              Status
            </label>
            <select
              id="user-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">All statuses</option>
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
              <option value="PendingVerification">Pending verification</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {!data ? (
        <Loading rows={6} />
      ) : data.items.length === 0 ? (
        <div className="admin-card text-muted">No accounts match these filters.</div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Roles</th>
                  <th scope="col">Verified</th>
                  <th scope="col">Listings</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="fw-semibold">{user.fullName}</div>
                      <div className="text-muted small">{user.email}</div>
                      {user.phone && <div className="text-muted small">{user.phone}</div>}
                    </td>

                    <td>
                      {user.roles.map((r) => (
                        <span className="badge bg-secondary me-1" key={r}>
                          {r}
                        </span>
                      ))}
                    </td>

                    <td className="small">
                      <div>{user.emailVerified ? <><IconTick /> email</> : <>— email</>}</div>
                      <div>{user.phoneVerified ? <><IconTick /> phone</> : <>— phone</>}</div>
                      {user.contactVerifiedByAdmin && (
                            <div className="text-success"><IconTick /> by admin</div>
                          )}
                    </td>

                    <td className="small">
                      {user.businessCount} business{user.businessCount === 1 ? '' : 'es'}
                      <div className="text-muted">{user.propertyCount} listings</div>
                    </td>

                    <td>
                      <span className={`badge bg-${user.status === 'Active' ? 'success' : 'danger'}`}>
                        {humanise(user.status)}
                      </span>
                      {user.statusReason && <div className="text-muted small mt-1">{user.statusReason}</div>}
                    </td>

                    <td className="text-muted small text-nowrap">{dateTime(user.lastLoginAtUtc)}</td>

                    <td>
                      <div className="d-flex flex-column gap-1">
                        {user.status === 'Active' ? (
                          !user.roles.includes('SuperAdmin') && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setSuspending(user)}
                            >
                              Suspend
                            </button>
                          )
                        ) : (
                          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => reactivate(user)}>
                            Reactivate
                          </button>
                        )}

                        {!user.contactVerifiedByAdmin && user.roles.includes('Owner') && (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => verify(user)}>
                            Mark verified
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager result={data} onPage={setPage} />
        </>
      )}

      {suspending && (
        <SuspendDialog
          user={suspending}
          onCancel={() => setSuspending(null)}
          onDone={async (message) => {
            setSuspending(null);
            setSuccess(message);
            await load();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

function SuspendDialog({
  user,
  onCancel,
  onDone,
  onError,
}: {
  user: AdminUser;
  onCancel: () => void;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 5) {
      onError('Give a reason of at least five characters. The account holder will see it.');
      return;
    }

    setBusy(true);

    try {
      await adminApi.suspendUser(user.id, reason.trim());
      await onDone(`${user.email} has been suspended and signed out everywhere.`);
    } catch (err) {
      onError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="suspend-title">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="suspend-title">
                Suspend this account?
              </h2>
            </div>

            <div className="modal-body">
              <p>
                <strong>{user.fullName}</strong> ({user.email}) will be signed out of every device and will not be
                able to sign in again until reactivated.
              </p>

              {user.propertyCount > 0 && (
                <div className="alert alert-warning py-2 px-3 small">
                  This account manages {user.propertyCount} listing(s). Suspending the account does not take those
                  listings down — suspend them individually if that is what you intend.
                </div>
              )}

              <label className="form-label" htmlFor="suspend-reason">
                Reason (shown to the account holder)
              </label>
              <textarea
                id="suspend-reason"
                className="form-control"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={submit} disabled={busy}>
                {busy ? 'Suspending…' : 'Suspend account'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
